/**
 * Barra de filtros da listagem operativa.
 * Cada campo notifica o pai via onFiltroChange — sem estado próprio,
 * exceto o dropdown de Sistemas que acumula a seleção e dispara ao fechar.
 * O debounce e a lógica de min-chars vivem no hook, não aqui.
 */
import React, { useRef, useEffect, useState } from "react";
import * as MUI from "@mui/material";
import * as Icon from "@mui/icons-material";

import {
  FiltrosOperativa,
  NivelAcesso,
  SistemaItem,
} from "../../../../interface/usuarioOperativa";

interface FiltrosUsuariosProps {
  filtros: FiltrosOperativa;
  loading: boolean;
  /** Exibe o filtro de BASE somente para usuários Master */
  exibirFiltroBase: boolean;
  sistemasDisponiveis: SistemaItem[];
  onFiltroChange<K extends keyof FiltrosOperativa>(
    chave: K,
    valor: FiltrosOperativa[K]
  ): void;
  onLimpar(): void;
}

const OPCOES_ACESSO = [
  { value: "" as NivelAcesso, label: "Todos" },
  { value: 0 as NivelAcesso, label: "Básico" },
  { value: 1 as NivelAcesso, label: "Admin" },
  { value: 2 as NivelAcesso, label: "Master" },
];

/**
 * Detecta se a string digitada representa um CPF.
 * Critério: conter 3 ou mais dígitos consecutivos.
 */
const isCpfInput = (valor: string): boolean => /\d{3,}/.test(valor);

export const FiltrosUsuarios: React.FC<FiltrosUsuariosProps> = ({
  filtros,
  loading,
  exibirFiltroBase,
  sistemasDisponiveis,
  onFiltroChange,
  onLimpar,
}) => {
  // Estado local do dropdown de sistemas — acumula seleção antes de fechar
  const [sistemasPendentes, setSistemasPendentes] = useState<number[]>(
    filtros.sistemas
  );

  // Sincroniza o estado local se os filtros forem resetados externamente (limparFiltros)
  useEffect(() => {
    setSistemasPendentes(filtros.sistemas);
  }, [filtros.sistemas]);

  // Guarda qual input estava focado antes do loading iniciar
  const focusedFieldRef = useRef<string | null>(null);
  const nomeRef = useRef<HTMLInputElement>(null);
  const cargoRef = useRef<HTMLInputElement>(null);
  const unidadeRef = useRef<HTMLInputElement>(null);
  const baseRef = useRef<HTMLInputElement>(null);

  const inputRefs: Record<string, React.RefObject<HTMLInputElement>> = {
    nome: nomeRef,
    cargo: cargoRef,
    nmUnidOperacional: unidadeRef,
    base: baseRef,
  };

  // Quando loading termina, restaura o foco no campo que estava ativo
  useEffect(() => {
    if (!loading && focusedFieldRef.current) {
      const ref = inputRefs[focusedFieldRef.current];
      ref?.current?.focus();
    }
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFocus = (field: string) => {
    focusedFieldRef.current = field;
  };

  // Dispara o filtro de sistemas somente quando o dropdown fecha
  const handleSistemasClose = () => {
    onFiltroChange("sistemas", sistemasPendentes);
  };

  const nomeIsCpf = isCpfInput(filtros.nome);
  const nomeHelperText = (() => {
    if (filtros.nome.length === 0) return undefined;
    if (filtros.nome.length < 3) return "Digite ao menos 3 caracteres";
    return nomeIsCpf ? "Filtrando por CPF" : "Filtrando por nome";
  })();

  return (
    <MUI.Paper variant="outlined" sx={{ p: 2 }}>
      <MUI.Grid container spacing={2} alignItems="flex-start">
        {/* Nome ou CPF */}
        <MUI.Grid item xs={12} sm={6} md={3}>
          <MUI.TextField
            label="Nome ou CPF"
            value={filtros.nome}
            onChange={(e) => onFiltroChange("nome", e.target.value)}
            onFocus={() => handleFocus("nome")}
            size="small"
            fullWidth
            disabled={loading}
            inputRef={nomeRef}
            InputProps={{
              startAdornment: (
                <MUI.InputAdornment position="start">
                  {nomeIsCpf && filtros.nome.length >= 3 ? (
                    <Icon.Badge fontSize="small" />
                  ) : (
                    <Icon.Search fontSize="small" />
                  )}
                </MUI.InputAdornment>
              ),
            }}
            helperText={nomeHelperText}
          />
        </MUI.Grid>

        {/* Cargo */}
        <MUI.Grid item xs={12} sm={6} md={2}>
          <MUI.TextField
            label="Cargo"
            value={filtros.cargo}
            onChange={(e) => onFiltroChange("cargo", e.target.value)}
            onFocus={() => handleFocus("cargo")}
            size="small"
            fullWidth
            disabled={loading}
            inputRef={cargoRef}
            InputProps={{
              startAdornment: (
                <MUI.InputAdornment position="start">
                  <Icon.Work fontSize="small" />
                </MUI.InputAdornment>
              ),
            }}
            helperText={
              filtros.cargo.length > 0 && filtros.cargo.length < 3
                ? "Digite ao menos 3 caracteres"
                : undefined
            }
          />
        </MUI.Grid>

        {/* Unidade Operacional */}
        <MUI.Grid item xs={12} sm={6} md={2}>
          <MUI.TextField
            label="Unidade Operacional"
            value={filtros.nmUnidOperacional}
            onChange={(e) => {
              onFiltroChange("nmUnidOperacional", e.target.value);
            }}
            onFocus={() => handleFocus("nmUnidOperacional")}
            size="small"
            fullWidth
            disabled={loading}
            inputRef={unidadeRef}
            InputProps={{
              startAdornment: (
                <MUI.InputAdornment position="start">
                  <Icon.AccountBalance fontSize="small" />
                </MUI.InputAdornment>
              ),
            }}
            helperText={
              filtros.nmUnidOperacional.length === 1
                ? "Digite ao menos 2 caracteres"
                : undefined
            }
          />
        </MUI.Grid>

        {/* Nível de Acesso */}
        <MUI.Grid item xs={12} sm={6} md={exibirFiltroBase ? 1 : 2}>
          <MUI.FormControl size="small" fullWidth disabled={loading}>
            <MUI.InputLabel shrink>Perfil</MUI.InputLabel>
            <MUI.Select
              label="Perfil"
              displayEmpty
              notched
              value={filtros.idAcesso}
              onChange={(e) => {
                onFiltroChange("idAcesso", e.target.value as NivelAcesso);
              }}
              renderValue={(val) =>
                OPCOES_ACESSO.find((op) => op.value === val)?.label ?? "Todos"
              }
            >
              {OPCOES_ACESSO.map((op) => (
                <MUI.MenuItem key={String(op.value)} value={op.value}>
                  {op.label}
                </MUI.MenuItem>
              ))}
            </MUI.Select>
          </MUI.FormControl>
        </MUI.Grid>

        {/* Base — só para Master */}
        {exibirFiltroBase && (
          <MUI.Grid item xs={12} sm={6} md={1}>
            <MUI.TextField
              label="Base"
              value={filtros.base}
              onChange={(e) => onFiltroChange("base", e.target.value)}
              onFocus={() => handleFocus("base")}
              size="small"
              fullWidth
              disabled={loading}
              inputRef={baseRef}
              placeholder="pm, pc, ..."
              InputProps={{
                startAdornment: (
                  <MUI.InputAdornment position="start">
                    <Icon.Shield fontSize="small" />
                  </MUI.InputAdornment>
                ),
              }}
              helperText={
                filtros.base.length === 1
                  ? "Digite ao menos 2 caracteres"
                  : undefined
              }
            />
          </MUI.Grid>
        )}

        {/* Sistemas — multi-select, dispara filtro ao fechar */}
        <MUI.Grid item xs={12} sm={6} md={2}>
          <MUI.FormControl size="small" fullWidth disabled={loading}>
            <MUI.InputLabel>Sistemas</MUI.InputLabel>
            <MUI.Select
              label="Sistemas"
              multiple
              value={sistemasPendentes}
              onChange={(e) => {
                const val = e.target.value;
                setSistemasPendentes(
                  typeof val === "string"
                    ? val.split(",").map(Number)
                    : (val as number[])
                );
              }}
              onClose={handleSistemasClose}
              renderValue={(selected) =>
                (selected as number[])
                  .map(
                    (id) =>
                      sistemasDisponiveis.find((s) => s.ID_SISTEMA === id)
                        ?.NM_SISTEMA ?? String(id)
                  )
                  .join(", ")
              }
              startAdornment={
                <MUI.InputAdornment position="start">
                  <Icon.Apps fontSize="small" />
                </MUI.InputAdornment>
              }
            >
              {sistemasDisponiveis.map((s) => (
                <MUI.MenuItem key={s.ID_SISTEMA} value={s.ID_SISTEMA}>
                  <MUI.Checkbox
                    checked={sistemasPendentes.includes(s.ID_SISTEMA)}
                    size="small"
                  />
                  <MUI.ListItemText primary={s.NM_SISTEMA} />
                </MUI.MenuItem>
              ))}
            </MUI.Select>
          </MUI.FormControl>
        </MUI.Grid>

        {/* Botão limpar */}
        <MUI.Grid item xs={12} sm={6} md="auto">
          <MUI.Button
            variant="outlined"
            color="secondary"
            startIcon={<Icon.FilterAltOff />}
            onClick={onLimpar}
            disabled={loading}
            size="small"
            fullWidth
          >
            Limpar filtros
          </MUI.Button>
        </MUI.Grid>
      </MUI.Grid>
    </MUI.Paper>
  );
};
